import React, { useState } from 'react';
import { CaretDown, CaretUp, ShieldCheck } from '@phosphor-icons/react';
import { ALL_MODULES, MODULE_LABELS, NONE, READ, MANAGE, countAccessibleModules, countManageableModules } from '../utils/permissions';

const LEVEL_OPTIONS = [
  { value: NONE, label: 'Sin acceso' },
  { value: READ, label: 'Solo lectura' },
  { value: MANAGE, label: 'Administrar' },
];

const ModulePermissionsEditor = ({ permissions, onChange, isAdminRole, isNewUser, isTemplateApplied }) => {
  const [expanded, setExpanded] = useState(isNewUser);

  const accessCount = countAccessibleModules(permissions);
  const manageCount = countManageableModules(permissions);

  const handleChange = (module, value) => {
    onChange({ ...permissions, [module]: value });
  };

  if (isAdminRole) {
    return (
      <div className="p-3 rounded-lg" style={{ background: 'var(--people-accent-subtle)', border: '1px solid var(--border-soft)' }}>
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} style={{ color: 'var(--people-primary)' }} />
          <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Permisos por modulo</span>
        </div>
        <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
          Los administradores tienen acceso total a todos los modulos.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg" style={{ border: '1px solid var(--border-soft)' }} data-testid="module-permissions-section">
      {/* Header (collapsible) */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 hover:bg-opacity-50 transition-colors"
        style={{ background: expanded ? 'var(--people-accent-subtle)' : 'transparent' }}
        data-testid="toggle-permissions-section"
      >
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} style={{ color: 'var(--people-primary)' }} />
          <div className="text-left">
            <span className="text-sm font-medium block" style={{ color: 'var(--text-primary)' }}>Permisos por modulo</span>
            {!expanded && (
              <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                {accessCount} modulos con acceso &middot; {manageCount} administrables
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!expanded && isTemplateApplied && (
            <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'var(--people-accent-light)', color: 'var(--people-primary)' }}>
              Plantilla del rol
            </span>
          )}
          {!expanded && !isTemplateApplied && permissions && Object.keys(permissions).length > 0 && (
            <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: '#FEF3C7', color: '#92400E' }}>
              Personalizados
            </span>
          )}
          {expanded ? <CaretUp size={14} style={{ color: 'var(--text-muted)' }} /> : <CaretDown size={14} style={{ color: 'var(--text-muted)' }} />}
        </div>
      </button>

      {/* Content */}
      {expanded && (
        <div className="p-3 pt-0">
          <p className="text-[11px] mb-3" style={{ color: 'var(--text-muted)' }}>
            Define que secciones puede visualizar o administrar este usuario.
          </p>
          <div className="space-y-1">
            {ALL_MODULES.map(mod => {
              const current = permissions?.[mod] || NONE;
              return (
                <div key={mod} className="flex items-center justify-between py-1.5 px-1" data-testid={`perm-row-${mod}`}>
                  <span className="text-[13px] font-medium" style={{ color: 'var(--text-primary)' }}>
                    {MODULE_LABELS[mod]}
                  </span>
                  <div className="flex rounded-lg overflow-hidden" style={{ border: '1px solid var(--border-soft)' }}>
                    {LEVEL_OPTIONS.map(opt => {
                      const isActive = current === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => handleChange(mod, opt.value)}
                          className="px-2.5 py-1 text-[11px] font-medium transition-all"
                          style={{
                            background: isActive
                              ? opt.value === MANAGE ? 'var(--people-primary)' : opt.value === READ ? '#E0E7FF' : 'var(--people-accent-subtle)'
                              : 'white',
                            color: isActive
                              ? opt.value === MANAGE ? 'white' : opt.value === READ ? '#3730A3' : 'var(--text-secondary)'
                              : 'var(--text-muted)',
                            borderRight: '1px solid var(--border-soft)',
                          }}
                          data-testid={`perm-${mod}-${opt.value.toLowerCase()}`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ModulePermissionsEditor;
